import {createContext, ReactNode, useContext, useEffect, useMemo} from "react";

type CreateKeyedStoreContextOptions<TStore, TProps> = {
    createStore: (props: TProps) => TStore
    getKey: (props: TProps) => string
    displayName: string
}

export function createKeyedStoreContext<TStore, TProps extends { children: ReactNode }>({
    createStore,
    getKey,
    displayName
}: CreateKeyedStoreContextOptions<TStore, TProps>) {
    const storeCache = new Map<string, TStore>()
    const Context = createContext<TStore | null>(null)

    function Provider(props: TProps) {
        const {children} = props
        const key = getKey(props)

        const store = useMemo(() => {
            const cachedStore = storeCache.get(key)

            if (cachedStore) {
                return cachedStore
            }

            const newStore = createStore(props)
            storeCache.set(key, newStore)

            return newStore
        }, [key, props])

        useEffect(() => {
            return () => {
                const cachedStore = storeCache.get(key)

                if (cachedStore === store) {
                    storeCache.delete(key)
                }
            }
        }, [key, store])

        return <Context.Provider value={store}>{children}</Context.Provider>
    }

    Provider.displayName = `${displayName}Provider`

    function useStore(): TStore {
        const context = useContext(Context)

        if (!context) {
            throw new Error(`use${displayName}Store must be used within ${displayName}Provider`)
        }

        return context
    }

    return {
        Context,
        Provider,
        useStore
    }
}
